import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

import Link from "../../../atoms/mui/Link";

import SectionHeader from "./SectionHeader";

const CtaButton = ({ cta, variant, onClick }) => {
  if (cta.onClick) {
    const handleClick = () => {
      cta.onClick();
      onClick?.();
    };
    return (
      <Button variant={variant} color="primary" fullWidth onClick={handleClick}>
        {cta.label}
      </Button>
    );
  }
  return (
    <Link
      href={cta.href}
      onClick={onClick}
      styleProps={{ width: "100%", display: "block" }}
    >
      <Button variant={variant} color="primary" fullWidth>
        {cta.label}
      </Button>
    </Link>
  );
};

const PlanCard = ({
  plan,
  palette,
  onPrimaryCtaClick,
  onSecondaryCtaClick
}) => {
  const isHighlighted = plan.highlighted === true;

  return (
    <Box
      id={plan.id}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        bgcolor: palette.background.paper,
        border: isHighlighted
          ? `2px solid ${palette.primary.main}`
          : `1px solid ${palette.backgroundGray.dark}`,
        borderRadius: 2,
        p: { xs: 3, md: 3.5 },
        height: "100%",
        boxShadow: isHighlighted ? 4 : 0,
        transform: { md: isHighlighted ? "translateY(-8px)" : "none" },
        scrollMarginTop: 96
      }}
    >
      {plan.label && (
        <Box
          sx={{
            position: "absolute",
            top: -12,
            left: 16,
            bgcolor: isHighlighted
              ? palette.primary.main
              : palette.backgroundGray.dark,
            color: isHighlighted
              ? palette.background.paper
              : palette.text.primary,
            px: 1.5,
            py: 0.5,
            borderRadius: 1
          }}
        >
          <Typography variant="overline" color="inherit">
            {plan.label}
          </Typography>
        </Box>
      )}

      <Box mt={1} mb={0.5}>
        <Typography variant="h5" color="textPrimary">
          {plan.name}
        </Typography>
      </Box>

      {plan.bestFor && (
        <Box mb={1}>
          <Typography variant="overline" color="textSecondary">
            Best for: {plan.bestFor}
          </Typography>
        </Box>
      )}

      {plan.description && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            {plan.description}
          </Typography>
        </Box>
      )}

      {plan.bullets && plan.bullets.length > 0 && (
        <Box
          component="ul"
          sx={{ m: 0, p: 0, listStyle: "none", mb: 3, flexGrow: 1 }}
        >
          {plan.bullets.map((bullet, i) => (
            <Box
              component="li"
              key={`bullet-${i}`}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}
            >
              <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                <FontAwesomeIcon
                  icon={faCheck}
                  style={{ color: palette.primary.main, fontSize: 14 }}
                />
              </Box>
              <Typography variant="body2" color="textPrimary">
                {bullet}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Stack spacing={1} sx={{ mt: "auto" }}>
        {plan.primaryCta && (
          <CtaButton
            cta={plan.primaryCta}
            variant="contained"
            onClick={onPrimaryCtaClick}
          />
        )}
        {plan.secondaryCta && (
          <CtaButton
            cta={plan.secondaryCta}
            variant="text"
            onClick={onSecondaryCtaClick}
          />
        )}
      </Stack>
    </Box>
  );
};

const PricingGrid = ({
  eyebrow,
  title,
  description,
  plans,
  background = "default",
  onPlanCtaClick
}) => {
  const { palette } = useTheme();
  const bgColor =
    background === "white"
      ? palette.background.paper
      : palette.backgroundGray.light;

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: bgColor }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: `repeat(${plans.length}, 1fr)`
            },
            gap: { xs: 3, md: 2.5 },
            alignItems: "stretch"
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              palette={palette}
              onPrimaryCtaClick={() =>
                onPlanCtaClick?.(plan, plan.primaryCta, "primary")
              }
              onSecondaryCtaClick={() =>
                onPlanCtaClick?.(plan, plan.secondaryCta, "secondary")
              }
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};
export default PricingGrid;
